import { useEffect, useState } from 'react';
import { WindSlashArc } from './WindSlashArc';

interface Slash {
  id: number;
  /** Z-axis tilt — positive = "\\" stroke, negative = "/" stroke. */
  tilt: number;
  /** Slight X-offset so left + right slashes don't completely overlap and
   *  the X-pattern reads as TWO visible blade arcs, not a single bright blob. */
  xOffset: number;
}

interface Props {
  /** Interval between dual-slash pulses, in ms. Default 4000. */
  intervalMs?: number;
  /** Forward offset along the player's facing axis. Both combat scene
   *  (player yawed to face aim) and the portrait (camera at +Z) treat the
   *  player's LOCAL +Z as forward, so positive values go forward. */
  forwardOffset?: number;
  /** Height above player feet — Seelen-Herz mesh sits around y=0.85 (chest),
   *  so the slashes spawn at chest-level to read as "out of the emblem". */
  height?: number;
}

/**
 * Idle dual-slash signature effect for the Seelen-Herz (`hk_heart`) accessory.
 *
 * Renders inside PlayerCharacter so it shows in BOTH the combat scene AND the
 * character portrait (anywhere PlayerCharacter mounts). No scene-level wiring.
 *
 * Visual pattern: every `intervalMs`, fire TWO vertical wind-slashes 140 ms
 * apart — one slanted "/" from the left half of the chest, one slanted "\\"
 * from the right half. Slight X-offset between them so they don't collapse
 * into a single additive-blended blob. Together they read as the two crossed
 * blades on the heart emblem briefly swinging forward.
 *
 * Local-space conventions (assumes PlayerCharacter group faces toward -Z, the
 * Three.js camera default): -Z is forward. Y is up.
 */
export function HeartDualSlashes({
  intervalMs = 4000,
  forwardOffset = 0.42,
  height = 0.88,
}: Props = {}) {
  const [slashes, setSlashes] = useState<Slash[]>([]);

  useEffect(() => {
    let idCounter = 1;
    const fire = () => {
      // First stroke — comes from the LEFT sword of the emblem: slash arcs
      // from upper-left down to lower-right. Z-rotation +π/5 tilts the plane
      // so the arc (which lives in the bottom half of the UV by default)
      // sweeps in that "\\" direction. Spawn from -X offset (left-of-chest).
      setSlashes((arr) => [...arr, { id: idCounter++, tilt:  Math.PI / 4, xOffset: -0.07 }]);
      // Second stroke — opposite: from the RIGHT sword, arcs "/" pattern.
      // 160 ms gap so the user sees them as SEQUENTIAL strokes that combine
      // into an X, not a single instantaneous burst.
      const second = window.setTimeout(() => {
        setSlashes((arr) => [...arr, { id: idCounter++, tilt: -Math.PI / 4, xOffset:  0.07 }]);
      }, 160);
      return () => window.clearTimeout(second);
    };

    const initial = window.setTimeout(fire, 1000);
    const tick = window.setInterval(fire, intervalMs);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(tick);
    };
  }, [intervalMs]);

  const remove = (id: number) =>
    setSlashes((arr) => arr.filter((s) => s.id !== id));

  return (
    <>
      {slashes.map((s) => (
        <WindSlashArc
          key={s.id}
          position={[s.xOffset, height, forwardOffset]}
          // Plane default normal is +Z = player FORWARD (wrists at z=+0.1
          // confirm this). No Y-flip → slash faces camera in portrait,
          // faces aim in combat. Z-tilt diagonalises the arc for "/" + "\\".
          rotation={[0, 0, s.tilt]}
          scale={0.32}
          durationMs={280}
          colorBase="#ffffff"
          colorHot="#e6f6ff"
          onComplete={() => remove(s.id)}
        />
      ))}
    </>
  );
}
